import { Component } from '@angular/core';
import { LucideAngularModule, Coins, Globe2, Zap, BarChart3, Flame, Store, Check } from 'lucide-angular';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-features',
  standalone: true,
  imports: [LucideAngularModule, TranslateModule],
  template: `
    <section id="features" class="py-20 md:py-28 bg-dark-100 relative overflow-hidden">
      <div class="max-w-7xl mx-auto px-5 md:px-10">
        
        <div class="max-w-xl mb-14" data-aos="fade-up">
          <p class="text-primary font-bold text-xs tracking-[0.2em] uppercase mb-3">{{ 'FEATURES.LABEL' | translate }}</p>
          <h2 class="text-3xl md:text-[2.75rem] font-black mb-4 leading-[1.1]">
            {{ 'FEATURES.TITLE' | translate }}
          </h2>
        </div>

        <!-- Bento grid -->
        <div class="grid md:grid-cols-12 gap-3">

          <!-- Hero card: Gagnez avant le streaming — spans 7 cols -->
          <div class="md:col-span-7 bg-dark-300 border border-white/[0.04] rounded-2xl p-7 relative overflow-hidden group hover:border-primary/20 transition-all duration-300" data-aos="fade-up" data-aos-delay="100">
            <!-- Subtle gradient accent -->
            <div class="absolute top-0 right-0 w-40 h-40 bg-primary/[0.04] rounded-full blur-[60px] pointer-events-none group-hover:bg-primary/[0.08] transition-all duration-500"></div>
            
            <div class="relative z-10">
              <div class="flex items-center gap-3 mb-5">
                <div class="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <lucide-icon [img]="Coins" class="w-5 h-5 text-white"></lucide-icon>
                </div>
                <span class="text-[10px] font-bold tracking-[0.15em] uppercase text-primary">{{ 'FEATURES.CARD_1_TAG' | translate }}</span>
              </div>
              <h3 class="text-xl font-black text-white mb-2">{{ 'FEATURES.CARD_1_TITLE' | translate }}</h3>
              <p class="text-sm text-gray-450 leading-relaxed mb-6 max-w-md">
                {{ 'FEATURES.CARD_1_DESC' | translate }}
              </p>

              <!-- Mini revenue visual -->
              <div class="flex items-end gap-1 h-10">
                <div class="w-2 bg-primary/20 rounded-sm" style="height:30%"></div>
                <div class="w-2 bg-primary/30 rounded-sm" style="height:50%"></div>
                <div class="w-2 bg-primary/40 rounded-sm" style="height:45%"></div>
                <div class="w-2 bg-primary/50 rounded-sm" style="height:70%"></div>
                <div class="w-2 bg-primary/60 rounded-sm" style="height:65%"></div>
                <div class="w-2 bg-primary/70 rounded-sm" style="height:80%"></div>
                <div class="w-2 bg-primary rounded-sm" style="height:100%"></div>
              </div>
            </div>
          </div>

          <!-- Distribution mondiale — spans 5 cols -->
          <div class="md:col-span-5 bg-dark-300 border border-white/[0.04] rounded-2xl p-7 relative overflow-hidden group hover:border-accent-cyan/20 transition-all duration-300" data-aos="fade-up" data-aos-delay="200">
            <div class="absolute bottom-0 left-0 w-32 h-32 bg-accent-cyan/[0.04] rounded-full blur-[50px] pointer-events-none"></div>
            
            <div class="relative z-10">
              <div class="w-10 h-10 rounded-lg bg-accent-cyan/10 flex items-center justify-center mb-5">
                <lucide-icon [img]="Globe2" class="w-5 h-5 text-accent-cyan"></lucide-icon>
              </div>
              <h3 class="text-base font-bold text-white mb-2">{{ 'FEATURES.CARD_2_TITLE' | translate }}</h3>
              <p class="text-sm text-gray-450 leading-relaxed mb-5">{{ 'FEATURES.CARD_2_DESC' | translate }}</p>
              
              <!-- Platform logos grid -->
              <div class="grid grid-cols-4 gap-2">
                <div class="bg-dark-100 rounded-lg p-2 flex items-center justify-center h-9">
                  <img src="other/Spotify_logo_with_text.svg.png" alt="Spotify" class="max-h-3.5 brightness-0 invert opacity-50" />
                </div>
                <div class="bg-dark-100 rounded-lg p-2 flex items-center justify-center h-9">
                  <img src="other/apple_music_logo.png" alt="Apple Music" class="max-h-3.5 brightness-0 invert opacity-50" />
                </div>
                <div class="bg-dark-100 rounded-lg p-2 flex items-center justify-center h-9">
                  <img src="other/Logo_Deezer_2023.svg" alt="Deezer" class="max-h-3.5 brightness-0 invert opacity-50" />
                </div>
                <div class="bg-dark-100 rounded-lg p-2 flex items-center justify-center h-9 text-gray-550 text-[10px] font-bold">
                  +4
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Money — spans 5 cols -->
          <div class="md:col-span-5 bg-dark-300 border border-white/[0.04] rounded-2xl p-7 relative overflow-hidden group hover:border-accent-green/20 transition-all duration-300" data-aos="fade-up" data-aos-delay="300">
            <div class="absolute top-0 left-0 w-28 h-28 bg-accent-green/[0.04] rounded-full blur-[50px] pointer-events-none"></div>
            
            <div class="relative z-10">
              <div class="w-10 h-10 rounded-lg bg-accent-green/10 flex items-center justify-center mb-5">
                <lucide-icon [img]="Zap" class="w-5 h-5 text-accent-green"></lucide-icon>
              </div>
              <h3 class="text-base font-bold text-white mb-2">{{ 'FEATURES.CARD_3_TITLE' | translate }}</h3>
              <p class="text-sm text-gray-450 leading-relaxed mb-5">{{ 'FEATURES.CARD_3_DESC' | translate }}</p>
              
              <!-- Transaction mock -->
              <div class="bg-dark-100 rounded-xl p-3 border border-white/[0.04]">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-2">
                    <img src="payment/omlogo.png" alt="OM" class="w-6 h-6 bg-white p-0.5 rounded-md object-contain" />
                    <span class="text-xs text-gray-350">Orange Money</span>
                  </div>
                  <span class="text-accent-green text-[10px] font-bold flex items-center gap-0.5">
                    <lucide-icon [img]="Check" class="w-2.5 h-2.5"></lucide-icon> {{ 'FEATURES.CARD_3_RECEIVED' | translate }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <img src="payment/momologo.png" alt="MoMo" class="w-6 h-6 bg-white p-0.5 rounded-md object-contain" />
                    <span class="text-xs text-gray-350">MTN MoMo</span>
                  </div>
                  <span class="text-accent-green text-[10px] font-bold flex items-center gap-0.5">
                    <lucide-icon [img]="Check" class="w-2.5 h-2.5"></lucide-icon> {{ 'FEATURES.CARD_3_RECEIVED' | translate }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Analytics — spans 4 cols -->
          <div class="md:col-span-4 bg-dark-300 border border-white/[0.04] rounded-2xl p-7 group hover:border-primary/20 transition-all duration-300" data-aos="fade-up" data-aos-delay="400">
            <div class="w-10 h-10 rounded-lg bg-accent-yellow/10 flex items-center justify-center mb-5">
              <lucide-icon [img]="BarChart3" class="w-5 h-5 text-accent-yellow"></lucide-icon>
            </div>
            <h3 class="text-base font-bold text-white mb-2">{{ 'FEATURES.CARD_4_TITLE' | translate }}</h3>
            <p class="text-sm text-gray-450 leading-relaxed mb-5">{{ 'FEATURES.CARD_4_DESC' | translate }}</p>
            
            <!-- Mini stats -->
            <div class="grid grid-cols-2 gap-2">
              <div class="bg-dark-100 rounded-lg px-3 py-2">
                <p class="text-accent-yellow text-lg font-black">84%</p>
                <p class="text-gray-650 text-[10px]">{{ 'FEATURES.CARD_4_MOBILE' | translate }}</p>
              </div>
              <div class="bg-dark-100 rounded-lg px-3 py-2">
                <p class="text-white text-lg font-black">1.2k</p>
                <p class="text-gray-650 text-[10px]">{{ 'FEATURES.CARD_4_UNIQUE' | translate }}</p>
              </div>
            </div>
          </div>

          <!-- Drops — spans 3 cols -->
          <div class="md:col-span-3 bg-dark-300 border border-white/[0.04] rounded-2xl p-7 group hover:border-accent-red/20 transition-all duration-300" data-aos="fade-up" data-aos-delay="500">
            <div class="w-10 h-10 rounded-lg bg-accent-red/10 flex items-center justify-center mb-5">
              <lucide-icon [img]="Flame" class="w-5 h-5 text-accent-red"></lucide-icon>
            </div>
            <h3 class="text-base font-bold text-white mb-2">{{ 'FEATURES.CARD_5_TITLE' | translate }}</h3>
            <p class="text-sm text-gray-450 leading-relaxed">{{ 'FEATURES.CARD_5_DESC' | translate }}</p>
          </div>

        </div>

      </div>
    </section>
  `
})
export class FeaturesComponent {
  readonly Coins = Coins;
  readonly Globe2 = Globe2;
  readonly Zap = Zap;
  readonly BarChart3 = BarChart3;
  readonly Flame = Flame;
  readonly Store = Store;
  readonly Check = Check;
}
